<template>
  <section class="market-hero">
    <div class="market-hero__band"></div>
    <b-container>
      <div class="market-identity">
        <div class="market-identity__logo">
          <b-img v-if="market.logo" :src="market.logo" :alt="market.visualName" />
          <span v-else class="market-identity__initial">{{ initial }}</span>
        </div>
        <div class="market-identity__body">
          <h1 class="market-identity__name">{{ market.visualName }}</h1>
          <ul class="market-identity__meta">
            <li v-if="market.address">
              <b-icon icon="geo-alt" class="mr-1" />
              <a
                v-if="market.addressUrl"
                :href="market.addressUrl"
                target="_blank"
                rel="noopener"
              >
                {{ market.address }}
              </a>
              <span v-else>{{ market.address }}</span>
            </li>
            <li v-if="openTodayText" :class="{ 'text-success': isOpenToday }">
              <b-icon icon="clock" class="mr-1" />
              {{ openTodayText }}
            </li>
          </ul>
        </div>
        <div v-if="market.instagramUrl || market.facebookUrl || market.websiteUrl" class="market-identity__links">
          <a
            v-if="market.instagramUrl"
            :href="market.instagramUrl"
            target="_blank"
            rel="noopener"
            class="market-identity__social"
            title="Instagram"
          >
            <font-awesome-icon :icon="['fab', 'instagram']" />
          </a>
          <a
            v-if="market.facebookUrl"
            :href="market.facebookUrl"
            target="_blank"
            rel="noopener"
            class="market-identity__social"
            title="Facebook"
          >
            <font-awesome-icon :icon="['fab', 'facebook-f']" />
          </a>
          <a
            v-if="market.websiteUrl"
            :href="market.websiteUrl"
            target="_blank"
            rel="noopener"
            class="market-identity__social"
            :title="market.websiteUrl"
          >
            <b-icon icon="globe" />
          </a>
        </div>
      </div>
    </b-container>
  </section>
</template>

<script>
  import moment from 'moment'

  export default {
    name: 'MarketHero',

    props: {
      market: {
        type: Object,
        required: true,
      },
    },

    computed: {
      initial () {
        return (this.market.visualName || '').trim().charAt(0).toUpperCase()
      },

      todayHours () {
        const todayCode = moment().isoWeekday()
        return (this.market.openingHours || []).find(hours => hours.weekdayCode === todayCode) || null
      },

      isOpenToday () {
        return Boolean(this.todayHours && this.todayHours.fromHour && this.todayHours.toHour)
      },

      openTodayText () {
        if (!this.market.openingHours || !this.market.openingHours.length)
          return null
        if (this.isOpenToday)
          return this.$t('pages.login.user.open_today', { from: this.todayHours.fromHour, to: this.todayHours.toHour })
        return this.$t('pages.login.user.closed_today')
      },
    },
  }
</script>

<style scoped>
  .market-hero__band {
    height: 120px;
    background: linear-gradient(180deg, var(--ui-brand-tint) 0%, var(--ui-brand-soft) 100%);
  }

  .market-identity {
    position: relative;
    margin-top: -48px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
    background: #fff;
    border: 1px solid var(--ui-border);
    border-radius: 12px;
    padding: 1.25rem;
    box-shadow: var(--ui-shadow);
  }

  .market-identity__logo {
    width: 72px;
    height: 72px;
    flex: 0 0 72px;
    border-radius: 12px;
    border: 1px solid var(--ui-border);
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .market-identity__logo img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .market-identity__initial {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--ui-brand);
  }

  .market-identity__body {
    flex: 1 1 200px;
    min-width: 0;
  }

  .market-identity__name {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--ui-ink);
    margin-bottom: 0.25rem;
    overflow-wrap: anywhere;
  }

  .market-identity__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 1.25rem;
    color: var(--ui-muted);
    font-size: 0.95rem;
  }

  .market-identity__meta li {
    display: inline-flex;
    align-items: center;
  }

  .market-identity__links {
    display: flex;
    gap: 0.5rem;
  }

  .market-identity__social {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    border: 1px solid var(--ui-border);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--ui-ink);
    font-size: 1.1rem;
  }

  .market-identity__social:hover,
  .market-identity__social:focus {
    border-color: var(--ui-brand);
    color: var(--ui-brand);
    text-decoration: none;
  }

  @media (min-width: 768px) {
    .market-hero__band {
      height: 160px;
    }

    .market-identity {
      margin-top: -56px;
      padding: 1.5rem;
    }

    .market-identity__logo {
      width: 96px;
      height: 96px;
      flex-basis: 96px;
    }

    .market-identity__name {
      font-size: 2rem;
    }
  }
</style>
